import { DetectionResult, DomainPreset } from "../types";
import {
  analyzeTextLocally,
  applyFeedbackCalibrationToResult,
  ModelWeights,
  DEFAULT_CLIENT_WEIGHTS
} from "./detectClient";

export interface ResilientScanResult {
  result: DetectionResult;
  source: "server" | "local-fallback" | "server-cached";
  computeMsSaved?: number;
  fallbackReason?: string;
}

/**
 * Zero-Downtime Detection Dispatcher:
 * Attempts server-side detection with Layer 9 cache & backpressure governor,
 * but if the server is restarting/draining or offline, automatically and instantaneously
 * falls back to client-side WebAssembly/JS NLP analysis with ZERO dropped scans or errors.
 */
export async function executeResilientDetection(
  text: string,
  weights: ModelWeights = DEFAULT_CLIENT_WEIGHTS,
  options: {
    lowConfidence?: number;
    highConfidence?: number;
    domainPreset?: DomainPreset;
    timeoutMs?: number;
  } = {}
): Promise<ResilientScanResult> {
  const timeoutMs = options.timeoutMs || 4000;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const res = await fetch("/api/detect", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        weights,
        low_confidence: options.lowConfidence,
        high_confidence: options.highConfidence
      }),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      const isCached = data.cached === true;
      const rawFormattedResult: DetectionResult = {
        overall_prediction: data.overall_prediction,
        confidence_score: data.confidence_score,
        segments: data.segments || [],
        features: data.features,
        explanation: data.explanation || "",
        isBorderline: data.isBorderline,
        needsHumanReview: data.needsHumanReview,
        reviewBand: data.reviewBand,
        feedbackShiftApplied: data.feedbackShiftApplied,
        calibratedVia: data.calibratedVia,
        verifiedGroundTruth: data.verifiedGroundTruth
      };

      // Always apply client-side verified feedback shift (-35% / +35%) & similarity memory
      const formattedResult = applyFeedbackCalibrationToResult(
        rawFormattedResult,
        text,
        options.lowConfidence,
        options.highConfidence
      );

      return {
        result: formattedResult,
        source: isCached ? "server-cached" : "server",
        computeMsSaved: data.computeMsSaved
      };
    }

    // If server returned 503 (draining or backpressure) or 502/504, activate zero-downtime local fallback
    console.warn(`[ZERO-DOWNTIME] Server returned status ${res.status}. Falling back to 100% on-device client engine.`);
    const localResult = analyzeTextLocally(text, weights, {
      lowConfidence: options.lowConfidence,
      highConfidence: options.highConfidence,
      domainPreset: options.domainPreset
    });

    return {
      result: localResult,
      source: "local-fallback",
      fallbackReason: `Server status ${res.status}: Continuous zero-downtime on-device analysis activated.`
    };
  } catch (netErr: any) {
    // Network timeout, AbortError, or server restarting
    console.info("[ZERO-DOWNTIME] Server API unreachable during rolling restart. Seamlessly executing on-device local engine:", netErr?.message);
    const localResult = analyzeTextLocally(text, weights, {
      lowConfidence: options.lowConfidence,
      highConfidence: options.highConfidence,
      domainPreset: options.domainPreset
    });

    return {
      result: localResult,
      source: "local-fallback",
      fallbackReason: "Continuous zero-downtime local execution (server connection momentarily paused)."
    };
  }
}
